import React, { useRef, useState } from "react";
import { Box, Grid, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import CenteredLoader from "@components/CenteredLoader";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import CardInformacionMoto from "@components/CardInformacionMoto";
import { MotorbikeCatalogItem } from "@interfaces/catalogs";
import { useLazyGetMotorbikeCatalogQuery } from "@api/catalogs";
import { useDispatch } from "react-redux";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { TaxSystemConfigResponse } from "@interfaces/response";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { generateUrlPortal } from "@helpers/funciones";
import { TAX_SYSTEMS_CODES } from "@helpers/constantes";

const SeleccionarMoto = () => {
  // hooks
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  // api hooks
  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [getMotorbikeCatalog] = useLazyGetMotorbikeCatalogQuery();

  // state
  const [activeIndex, setActiveIndex] = useState(0);
  const [taxSystemConfig, setTaxSystemConfig] = useState<TaxSystemConfigResponse | null>(null);
  const [motorbikeCatalog, setMotorbikeCatalog] = useState<MotorbikeCatalogItem[]>([]);
  const [isFetchingCatalog, setIsFetchingCatalog] = useState(true);

  const handleScroll = () => {
    if (!scrollContainerRef.current || motorbikeCatalog.length === 0) return;
    const container = scrollContainerRef.current;
    const pageWidth = container.clientWidth;
    if (pageWidth === 0) return;
    const index = Math.round(container.scrollLeft / pageWidth);
    const safeIndex = Math.max(0, Math.min(index, motorbikeCatalog.length - 1));
    setActiveIndex(safeIndex);
  };

  const scrollToMotoIndex = (index: number) => {
    if (!scrollContainerRef.current || motorbikeCatalog.length === 0) return;
    const container = scrollContainerRef.current;
    const pageWidth = container.clientWidth;
    const safeIndex = Math.max(0, Math.min(index, motorbikeCatalog.length - 1));
    container.scrollTo({ left: safeIndex * pageWidth, behavior: "smooth" });
  };

  const fetchData = React.useCallback(async () => {
    setIsFetchingCatalog(true);
    try {
      const [taxSystemConfigResp, motorbikeCatalogResp] = await Promise.all([
        getTaxSystemConfig(TAX_SYSTEMS_CODES.CREDIMOTO_PF).unwrap(),
        getMotorbikeCatalog().unwrap(),
      ]);

      setTaxSystemConfig(taxSystemConfigResp);
      setMotorbikeCatalog(motorbikeCatalogResp);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "SeleccionarMoto-fetchData",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsFetchingCatalog(false);
    }
  }, [getMotorbikeCatalog, getTaxSystemConfig, dispatch]);

  const handleSelectMotorbike = (selectedMotorbike: MotorbikeCatalogItem) => {
    if (!taxSystemConfig) {
      dispatch(setModalErrorVisible({ open: true, error: null }));
      return;
    }

    const urlPortal = generateUrlPortal({
      taxSystemConfig,
      loan_amount: selectedMotorbike.data?.price || 0,
      loan_term: taxSystemConfig.defaultTerm,
      motorbike_catalog_id: selectedMotorbike.id,
    });

    navigate(urlPortal);
  };

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 1,
            mx: { xs: 1.5, md: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={"primary.dark"}
          >
            Selecciona tu moto
          </Typography>

          {isFetchingCatalog ? (
            <CenteredLoader sx={{ mt: 3 }} />
          ) : isMobileOrTablet ? (
            <Box width="100%" mt={3}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <IconButton
                  aria-label="Moto anterior"
                  onClick={() => scrollToMotoIndex(activeIndex - 1)}
                  disabled={activeIndex === 0 || motorbikeCatalog.length === 0}
                  color="primary"
                  size="small"
                  sx={{ flexShrink: 0 }}
                >
                  <KeyboardArrowLeft fontSize="large" />
                </IconButton>

                <Box
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    overflowX: "auto",
                    scrollSnapType: "x mandatory",
                    scrollBehavior: "smooth",
                    "&::-webkit-scrollbar": { display: "none" },
                    scrollbarWidth: "none",
                  }}
                >
                  {motorbikeCatalog.map((moto) => (
                    <Box
                      key={moto.id}
                      sx={{
                        flex: "0 0 100%",
                        minWidth: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        display: "flex",
                        justifyContent: "center",
                        scrollSnapAlign: "start",
                        scrollSnapStop: "always",
                        pb: 1,
                      }}
                    >
                      <CardInformacionMoto item={moto} onSelect={handleSelectMotorbike} size="small" />
                    </Box>
                  ))}
                </Box>

                <IconButton
                  aria-label="Moto siguiente"
                  onClick={() => scrollToMotoIndex(activeIndex + 1)}
                  disabled={activeIndex >= motorbikeCatalog.length - 1 || motorbikeCatalog.length === 0}
                  color="primary"
                  size="small"
                  sx={{ flexShrink: 0 }}
                >
                  <KeyboardArrowRight fontSize="large" />
                </IconButton>
              </Box>

              <Box display="flex" justifyContent="center" gap={1} mt={1.5}>
                {Array.from({ length: motorbikeCatalog.length }).map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      bgcolor: index === activeIndex ? "primary.light" : "#C9D8F5",
                      transition: "all .2s ease",
                    }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                mt: 3,
                display: "grid",
                gap: 2,
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              }}
            >
              {motorbikeCatalog.map((moto) => (
                <Box key={moto.id} display="flex" justifyContent="center">
                  <CardInformacionMoto item={moto} onSelect={handleSelectMotorbike} size="large" />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default SeleccionarMoto;
